import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, MapPin, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_KEYS = ["sports", "infra", "finance", "fraud", "diligence", "other"];

export const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t.contact.missingToast);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.successToast);
      setForm({ name: "", email: "", company: "", interest: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.contact.errorToast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative isolate overflow-hidden bg-[color:var(--ivory)] py-24 sm:py-32"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 top-1/4 h-[420px] w-[420px] rounded-full opacity-40 blur-3xl"
        style={{
          background:
            "radial-gradient(closest-side, rgba(13,59,46,0.18), rgba(13,59,46,0) 70%)",
        }}
      />

      <div className="relative mx-auto grid max-w-[1200px] grid-cols-1 gap-14 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10">
        <div className="lg:col-span-5">
          <div className="mb-6 flex items-baseline gap-6">
            <span className="eyebrow">{t.contact.eyebrow}</span>
            <div className="hairline-rule flex-1" />
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            viewport={{ once: true, margin: "-100px" }}
            data-testid="contact-headline"
            className="font-display text-[32px] leading-[1.05] text-[color:var(--ink)] sm:text-[42px] lg:text-[52px]"
          >
            {t.contact.title_a}{" "}
            <span className="font-serif-accent italic text-[color:var(--emerald)]">
              {t.contact.title_em}
            </span>
          </motion.h2>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-[color:var(--ink-3)] sm:text-base">
            {t.contact.sub}
          </p>

          <dl className="mt-10 space-y-4 text-sm text-[color:var(--ink-2)]">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-[color:var(--emerald)]" />
              <a
                href="mailto:hello@delhigence.ai"
                data-testid="contact-email-link"
                className="font-medium transition-colors hover:text-[color:var(--emerald)]"
              >
                hello@delhigence.ai
              </a>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="h-4 w-4 text-[color:var(--emerald)]" />
              <span className="font-medium">{t.contact.loc}</span>
            </div>
          </dl>
        </div>

        <div className="lg:col-span-7">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            viewport={{ once: true, margin: "-100px" }}
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="relative rounded-[6px] border border-[color:var(--rule-strong)] bg-[color:var(--paper)] p-7 shadow-[0_30px_80px_-50px_rgba(14,31,27,0.35)] sm:p-10"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <Label
                  htmlFor="name"
                  className="font-mono-accent text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]"
                >
                  {t.contact.fields.name}
                </Label>
                <Input
                  id="name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder={t.contact.fields.namePh}
                  className="mt-2 border-[color:var(--rule-strong)] bg-[color:var(--ivory)] text-[color:var(--ink)] placeholder:text-[color:var(--muted-2)] focus-visible:ring-[color:var(--emerald)]"
                  required
                />
              </div>
              <div>
                <Label
                  htmlFor="email"
                  className="font-mono-accent text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]"
                >
                  {t.contact.fields.email}
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder={t.contact.fields.emailPh}
                  className="mt-2 border-[color:var(--rule-strong)] bg-[color:var(--ivory)] text-[color:var(--ink)] placeholder:text-[color:var(--muted-2)] focus-visible:ring-[color:var(--emerald)]"
                  required
                />
              </div>
              <div>
                <Label
                  htmlFor="company"
                  className="font-mono-accent text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]"
                >
                  {t.contact.fields.company}
                </Label>
                <Input
                  id="company"
                  data-testid="contact-input-company"
                  value={form.company}
                  onChange={update("company")}
                  placeholder={t.contact.fields.companyPh}
                  className="mt-2 border-[color:var(--rule-strong)] bg-[color:var(--ivory)] text-[color:var(--ink)] placeholder:text-[color:var(--muted-2)] focus-visible:ring-[color:var(--emerald)]"
                />
              </div>
              <div>
                <Label
                  htmlFor="interest"
                  className="font-mono-accent text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]"
                >
                  {t.contact.fields.interest}
                </Label>
                <Select
                  value={form.interest}
                  onValueChange={(v) => update("interest")(v)}
                >
                  <SelectTrigger
                    id="interest"
                    data-testid="contact-select-interest"
                    className="mt-2 border-[color:var(--rule-strong)] bg-[color:var(--ivory)] text-[color:var(--ink)] focus:ring-[color:var(--emerald)]"
                  >
                    <SelectValue placeholder={t.contact.fields.interestPh} />
                  </SelectTrigger>
                  <SelectContent className="border-[color:var(--rule-strong)] bg-[color:var(--paper)] text-[color:var(--ink)]">
                    {INTEREST_KEYS.map((k) => (
                      <SelectItem
                        key={k}
                        value={t.contact.interests[k]}
                        data-testid={`contact-interest-${k}`}
                      >
                        {t.contact.interests[k]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-5">
              <Label
                htmlFor="message"
                className="font-mono-accent text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]"
              >
                {t.contact.fields.message}
              </Label>
              <Textarea
                id="message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={update("message")}
                placeholder={t.contact.fields.messagePh}
                rows={4}
                className="mt-2 border-[color:var(--rule-strong)] bg-[color:var(--ivory)] text-[color:var(--ink)] placeholder:text-[color:var(--muted-2)] focus-visible:ring-[color:var(--emerald)]"
                required
              />
            </div>

            <div className="mt-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              <p className="text-xs text-[color:var(--muted)]">{t.contact.nda}</p>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--emerald)] px-7 py-3.5 text-sm font-semibold text-[color:var(--paper)] transition-all hover:bg-[color:var(--ink)] hover:shadow-[0_18px_40px_-18px_rgba(13,59,46,0.55)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.contact.sending}
                  </>
                ) : (
                  <>
                    {t.contact.submit}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
